import React, { Component } from 'react';
import { arrayOf, shape, string, number, func, bool, objectOf } from 'prop-types';
import { orderBy, pick } from 'lodash';

import BaseDataTable from '../../BaseDataTable';
import TableHead from './KeywordTableHead';
import TableRow from './KeywordTableRow';
import TableSummary from './KeywordTableSummary';
import ModuleExport from '../../ModuleExport';
import { normalizeMetric } from '../../../helpers/validate';
import { calculateTableRows } from '../../DashboardTab/Report/helper';
import { SINGLE_PAGE_WEB_ANALYTICS_DATA } from '../../../helpers/constant';
import { SORT_BY_AD_VISIT, KEYWORD_CHART_ID } from '../../../helpers/constant';
import { shapeOfKeywordData } from '../constant';
import { Tooltip } from '@material-ui/core';
import { Info as InfoIcon } from '@material-ui/icons';
import {
  AD_VISIT_KEYWORD_INFO
}from 'components/DashboardTab/ChannelsAnalytics/constants.js';

import styles from './styles.scss';
export class KeywordTable extends Component {
  static propTypes = {
    data: arrayOf(shapeOfKeywordData).isRequired,
    onCellClick: func,
    fileName: string,
    initialSortStatus: shape({
      sortKey: string,
      sortType: string
    }),
    inpdf: bool,
    isGrandTotalDisplay: bool,
    rowsPerPage: number,
    page: number,
    beginKeyword: number,
    isCustomReport: bool,
    itemIndex: number,
    tableColumns: objectOf(shape({
      title: string,
      key: string
    })),
    isNewAdUsersSelected: bool,
    rowsFirstPage: number
  }

  static defaultProps = {
    fileName: 'keyword_data_table',
  }

  prepData() {
    const {
      data = [],
      inpdf,
      page,
      beginKeyword,
      rowsPerPage,
      isCustomReport,
      initialSortStatus,
      isNewAdUsersSelected,
      rowsFirstPage
    } = this.props;

    const dataSet = data && data.reduce((accum, currEntry) => {
      const { keywordValue, visits, adVisits, newUsers, newAdUsers } = currEntry || {};
      accum.tableData.push({
        keywordValue,
        adVisits,
        visits,
        newAdUsers,
        newUsers
      });
      accum.summaryData.adVisits += adVisits || 0;
      accum.summaryData.visits += visits || 0;
      accum.summaryData.newUsers += newUsers || 0;
      accum.summaryData.newAdUsers += newAdUsers || 0;
      return accum;
    }, { tableData: [], summaryData: { adVisits: 0, visits: 0, newUsers: 0, newAdUsers: 0 }});
    
    if (inpdf) {
      const { tableRowsFirstPage, tableRowsOtherPage } = calculateTableRows({ displayChart: false, isCustomReport, minRows:rowsFirstPage || SINGLE_PAGE_WEB_ANALYTICS_DATA })
      const { sortKey, sortType } = initialSortStatus || {};
      const sortedData = orderBy(dataSet.tableData, [sortKey], sortType);
      if (page === beginKeyword) {
        dataSet.tableData = sortedData.slice(0, rowsPerPage);
      } else {
        const startIndex = (tableRowsFirstPage + 1) + (page - beginKeyword - 1) * (tableRowsOtherPage + 1);
        dataSet.tableData = sortedData.slice(startIndex, startIndex + rowsPerPage);
      }
    }
    return dataSet;
  }

  handleCellClick = ({ data }) => {
    const { onCellClick } = this.props;
    onCellClick && onCellClick(data);
  }

  prepCsvData(data, headers) {
    const { initialSortStatus } = this.props;
    const { sortKey, sortType } = initialSortStatus || {};
    const sortedData = orderBy(data, [sortKey], sortType);
    const csvHeaders = headers.map(item => item.title);
    const normalizeData = sortedData.map(item => {
      const row = [];
      for (const [key, value] of Object.entries(item)) {
        if (key === 'keywordValue') {
          row.push(value);
        } else {
          row.push(
            normalizeMetric(value)
          )
        }
      }
      return row;
    })
    
    return [
      csvHeaders,
      ...normalizeData
    ];
  }

  tableExportContainer = React.createRef();
  render() {
    const {
      inpdf,
      isGrandTotalDisplay,
      fileName,
      initialSortStatus,
      rowsPerPage,
      page,
      itemIndex,
      tableColumns
    } = this.props;
    const dataSet = this.prepData();
    /* Handle column to display */
    const columnsToDisplay = Object.keys(tableColumns);
    const headerDisplay = columnsToDisplay.map(item => {
      const { key } = item;
      if (key === 'adVisits') {
        return {
          ...tableColumns[item],
          icon: (
            <Tooltip title={AD_VISIT_KEYWORD_INFO}>
              <InfoIcon className={styles.adsIcon}/>
            </Tooltip>
          )
        }
      } else {
        return tableColumns[item];
      }
    })
    const summaryDataDisplay = pick(dataSet.summaryData, columnsToDisplay);
    const tableContentDisplay = dataSet.tableData.map(item => pick(item, columnsToDisplay));
    const csvDisplay = this.prepCsvData(tableContentDisplay, headerDisplay);
    
    return (
      <div>
        {
          !inpdf && (
          <ModuleExport
            exportImg={this.tableExportContainer}
            data={csvDisplay}
            fileName={fileName}
            exportContainerStyle={{padding: '0 20px 0 0'}}
          />
          )
        }
        <div className={styles.tableContainer} ref={this.tableExportContainer}>
          <BaseDataTable 
            data={tableContentDisplay}
            tableHead={<TableHead headers={headerDisplay} />}
            tableRow={
              <TableRow columnsToDisplay={columnsToDisplay}/>
            }
            tableSummary={
              <TableSummary
                summaryData={summaryDataDisplay}
                columnsToDisplay={columnsToDisplay.filter(item => item !== 'keywordValue')}
              />
            }
            onBodyCellClick={this.handleCellClick}
            initialSortStatus={initialSortStatus}
            inpdf={inpdf}
            isGrandTotalDisplay={isGrandTotalDisplay}
            pagination
            rowsPerPage={rowsPerPage}
            page={page}
            tableIndex={itemIndex}
            tableId={KEYWORD_CHART_ID}
          />
        </div>
      </div>
    )
  }
}

import { connect } from 'react-redux';
import { getTableSortSelector } from 'components/DashboardTab/Report/helper';
import { INIT_NEW_AD_USERS_SORT } from '../../DashboardTab/ChannelsAnalytics/constants';

function mapStateToProps(state, props) {
  const tableSortSelector = getTableSortSelector(state, props, KEYWORD_CHART_ID);
  const defaultSortStatus = props.isNewAdUsersSelected ? INIT_NEW_AD_USERS_SORT : SORT_BY_AD_VISIT;
  return {
      tableSortSelector,
      initialSortStatus: tableSortSelector || defaultSortStatus
  }
}

export default connect(mapStateToProps)(KeywordTable);
